(function() {
  const ExistingSection = customElements.get('thunderbolt-formal-section');

  class ThunderboltFormalSection extends HTMLElement {
    connectedCallback() {
      if (this.__tbFormalInited) return;
      this.__tbFormalInited = true;
      this.syncViewportVars();
      this.initSwipers();
      this.initStickyBar();
      this.initSmoothScroll();
      this.initStickyNav();
      this.initTabs();
      this.initVideoModal();
      this.initSpecsMobile();
      this.initZoomVideoShowcase();
      this.initBreathingLightShowcase();
      this.initFaq();
      this.initAnimations();
      this.initUltraVideo();
      this.initEmailSignUp();
      if (this.dataset.tbViewportBound !== '1') {
        this.dataset.tbViewportBound = '1';
        window.addEventListener('resize', () => this.syncViewportVars(), { passive: true });
      }
    }

    initSwipers() {
      this.initFlagshipSwiper();
      this.initMediaVoicesSwiper();
      this.initProductivitySwiper();
    }

    syncViewportVars() {
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      this.style.setProperty('--tb-scrollbar-w', scrollbar + 'px');
    }

    runWhenSwiperReady(callback) {
      let done = false;
      const finish = () => {
        if (done) {
          return;
        }
        if (typeof Swiper === 'undefined') {
          return;
        }
        done = true;
        try {
          callback();
        } catch (err) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[thunderbolt-formal] Swiper init failed', err);
          }
        }
      };
      finish();
      if (done) {
        return;
      }
      const pollId = window.setInterval(() => {
        finish();
        if (done) {
          window.clearInterval(pollId);
        }
      }, 48);
      window.setTimeout(() => {
        window.clearInterval(pollId);
      }, 12000);
      window.addEventListener('load', finish, { once: true });
      window.requestAnimationFrame(finish);
    }

    initFlagshipSwiper() {
      const flagshipSwiperEls = this.querySelectorAll('.tb-flagship-swiper');
      if (!flagshipSwiperEls.length) return;

      this.runWhenSwiperReady(() => {
        flagshipSwiperEls.forEach((flagshipSwiperEl) => {
          const sectionEl = flagshipSwiperEl.closest('.tb-flagship-section') || this;
          const mobileLayoutMode = (sectionEl.dataset.mbLayout || 'slider').toLowerCase();
          const shouldUseMobileGrid = () => window.innerWidth < 1024 && mobileLayoutMode === 'grid';
          const syncMobileModeClass = () => {
            sectionEl.classList.toggle('is-mb-grid', shouldUseMobileGrid());
          };
          syncMobileModeClass();

          if (sectionEl.dataset.tbFlagshipResizeBound !== '1') {
            sectionEl.dataset.tbFlagshipResizeBound = '1';
            window.addEventListener('resize', () => {
              syncMobileModeClass();
              const currentSwiper = flagshipSwiperEl.swiper;
              if (shouldUseMobileGrid()) {
                if (currentSwiper) currentSwiper.destroy(true, true);
                flagshipSwiperEl.dataset.tbSwiperInited = '0';
                return;
              }
              if (!currentSwiper) {
                flagshipSwiperEl.dataset.tbSwiperInited = '0';
                this.initFlagshipSwiper();
                return;
              }
              if (currentSwiper.__tbApplyFlagshipAlign) currentSwiper.__tbApplyFlagshipAlign();
              currentSwiper.update();
            }, { passive: true });
          }

          if (shouldUseMobileGrid()) {
            const currentSwiper = flagshipSwiperEl.swiper;
            if (currentSwiper) currentSwiper.destroy(true, true);
            flagshipSwiperEl.dataset.tbSwiperInited = '0';
            return;
          }

          if (flagshipSwiperEl.dataset.tbSwiperInited === '1') return;

          const thumbs = sectionEl.querySelectorAll('.tb-flagship-thumb');
          const prevBtn = sectionEl.querySelector('.tb-flagship-prev');
          const nextBtn = sectionEl.querySelector('.tb-flagship-next');
          const paginationEl = sectionEl.querySelector('.tb-flagship-pagination');
          const slides = flagshipSwiperEl.querySelectorAll('.swiper-slide');
          const updateActiveThumb = (index) => {
            if (!thumbs.length) return;
            thumbs.forEach((thumb, i) => {
              thumb.classList.toggle('swiper-slide-thumb-active', i === index);
            });
          };
          const updateNavDisabled = (swiper) => {
            const toggleDisabled = (button, disabled) => {
              if (!button) return;
              button.classList.toggle('is-disabled', disabled);
              button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
            };
            toggleDisabled(prevBtn, swiper.isBeginning);
            toggleDisabled(nextBtn, swiper.isEnd);
          };
          const syncFlagshipContentState = (swiper, revealActiveContent) => {
            if (!swiper || !swiper.slides || !swiper.slides.length) return;
            swiper.slides.forEach((slide, index) => {
              const shouldReveal = revealActiveContent && index === swiper.activeIndex;
              slide.classList.toggle('is-content-ready', shouldReveal);
            });
          };

          /** 桌面通屏轮播：与上方 1280 版心对齐，不用 centeredSlides（避免相对视口居中与版心不一致） */
          const applyFlagshipColumnAlign = (swiper) => {
            if (!swiper || !swiper.el) return;
            if (window.innerWidth < 1024) {
              if (swiper.params.slidesOffsetBefore !== 0 || swiper.params.slidesOffsetAfter !== 0) {
                swiper.params.slidesOffsetBefore = 0;
                swiper.params.slidesOffsetAfter = 0;
                swiper.update();
              }
              return;
            }
            const slide = swiper.slides && swiper.slides[0];
            if (!slide) return;
            const cw = swiper.el.clientWidth;
            const sw = slide.offsetWidth;
            const offset = Math.max(0, Math.round((cw - sw) / 2));
            if (swiper.params.slidesOffsetBefore !== offset || swiper.params.slidesOffsetAfter !== offset) {
              swiper.params.slidesOffsetBefore = offset;
              swiper.params.slidesOffsetAfter = offset;
              swiper.update();
            }
          };

          const swiper = new Swiper(flagshipSwiperEl, {
            slidesPerView: 'auto',
            centeredSlides: false,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0,
            loop: false,
            rewind: true,
            watchOverflow: true,
            roundLengths: true,
            spaceBetween: 24,
            speed: 600,
            autoplay: {
              delay: 8000,
              disableOnInteraction: false
            },
            breakpoints: {
              320: {
                slidesPerView: 'auto',
                centeredSlides: false,
                spaceBetween: 12
              },
              1024: {
                slidesPerView: 'auto',
                centeredSlides: false,
                roundLengths: true,
                spaceBetween: 24
              }
            },
            navigation: {
              nextEl: nextBtn,
              prevEl: prevBtn
            },
            pagination: paginationEl ? {
              el: paginationEl,
              clickable: true,
              renderBullet: function(index, className) {
                if (index >= slides.length) return '';
                return '<span class="' + className + '"></span>';
              }
            } : undefined,
            on: {
              init: function() {
                const inst = this;
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => applyFlagshipColumnAlign(inst));
                });
                updateActiveThumb(this.realIndex);
                updateNavDisabled(this);
                syncFlagshipContentState(this, true);
              },
              slideChangeTransitionStart: function() {
                updateActiveThumb(this.realIndex);
                updateNavDisabled(this);
                syncFlagshipContentState(this, true);
              },
              slideChangeTransitionEnd: function() {
                syncFlagshipContentState(this, true);
              },
              slideChange: function() {
                updateActiveThumb(this.realIndex);
                updateNavDisabled(this);
              },
              resize: function() {
                applyFlagshipColumnAlign(this);
                updateNavDisabled(this);
                syncFlagshipContentState(this, true);
              }
            }
          });

          swiper.__tbApplyFlagshipAlign = () => applyFlagshipColumnAlign(swiper);

          thumbs.forEach((thumb, i) => {
            thumb.addEventListener('click', () => swiper.slideTo(i));
          });

          flagshipSwiperEl.dataset.tbSwiperInited = '1';
        });
      });
    }

    initMediaVoicesSwiper() {
      let mvSwiperEls = Array.from(this.querySelectorAll('.tb-mv-swiper'));
      if (!mvSwiperEls.length) {
        const globalCandidates = Array.from(document.querySelectorAll('.tb-mv-swiper'));
        mvSwiperEls = globalCandidates.filter((el) => {
          const host = el.closest('thunderbolt-formal-section');
          return !host || host === this || (this.id && host.id === this.id);
        });
      }
      if (!mvSwiperEls.length) {
        this.__tbMvRetryCount = (this.__tbMvRetryCount || 0) + 1;
        if (this.__tbMvRetryCount <= 3) {
          window.setTimeout(() => this.initMediaVoicesSwiper(), 400);
        }
        return;
      }
      this.__tbMvRetryCount = 0;

      this.runWhenSwiperReady(() => {
        mvSwiperEls.forEach((mvSwiperEl) => {
          if (mvSwiperEl.dataset.tbSwiperInited === '1') return;

          const sectionEl = mvSwiperEl.closest('.tb-media-voices-section') || this;
          const wrapperEl = mvSwiperEl.querySelector('.swiper-wrapper');
          const slides = mvSwiperEl.querySelectorAll('.swiper-slide');
          if (!wrapperEl || !slides.length) return;
          const topNextEl = sectionEl.querySelector('.tb-mv-header .tb-mv-next');
          const topPrevEl = sectionEl.querySelector('.tb-mv-header .tb-mv-prev');
          const bottomNextEl = sectionEl.querySelector('.tb-mv-next-bottom');
          const bottomPrevEl = sectionEl.querySelector('.tb-mv-prev-bottom');
          const paginationEl = sectionEl.querySelector('.tb-mv-pagination');
          // Calculate dynamic offset based on container padding (5.3333vw) or fixed 20px fallback
          const getMobileOffset = () => {
            if (window.innerWidth >= 1024) return 0;
            // Formula: 20 * 2 / 750 * 100 = 5.33333... vw
            const vw = window.innerWidth;
            return vw * (20 * 2 / 750); 
          };

          const mvSwiper = new Swiper(mvSwiperEl, {
            slidesPerView: 1.3,
            centeredSlides: false,
            spaceBetween: 12,
            slidesOffsetBefore: getMobileOffset(),
            slidesOffsetAfter: getMobileOffset(),
            speed: 600,
            loop: false,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            observeSlideChildren: true,
            pagination: paginationEl ? {
              el: paginationEl,
              clickable: true
            } : undefined,
            breakpoints: {
              768: {
                slidesPerView: 2,
                spaceBetween: 20,
                centeredSlides: false,
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 20,
                centeredSlides: false,
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0
              }
            },
            on: {
              resize: function() {
                const offset = getMobileOffset();
                if (this.params.slidesOffsetBefore !== offset) {
                  this.params.slidesOffsetBefore = offset;
                  this.params.slidesOffsetAfter = offset;
                  this.update();
                }
              }
            }
          });

          const nextButtons = Array.from(new Set([topNextEl, bottomNextEl].filter(Boolean)));
          const prevButtons = Array.from(new Set([topPrevEl, bottomPrevEl].filter(Boolean)));
          nextButtons.forEach((button) => {
            button.addEventListener('click', () => mvSwiper.slideNext());
          });
          prevButtons.forEach((button) => {
            button.addEventListener('click', () => mvSwiper.slidePrev());
          });

          const updateNavState = () => {
            if (mvSwiper.params.loop) {
              prevButtons.forEach((button) => button.classList.remove('swiper-button-disabled'));
              nextButtons.forEach((button) => button.classList.remove('swiper-button-disabled'));
            } else {
              prevButtons.forEach((button) => button.classList.toggle('swiper-button-disabled', mvSwiper.isBeginning));
              nextButtons.forEach((button) => button.classList.toggle('swiper-button-disabled', mvSwiper.isEnd));
            }
          };
          mvSwiper.on('slideChange', updateNavState);
          mvSwiper.on('reachBeginning', updateNavState);
          mvSwiper.on('reachEnd', updateNavState);
          mvSwiper.on('fromEdge', updateNavState);
          updateNavState();

          window.addEventListener('resize', () => mvSwiper.update(), { passive: true });
          mvSwiperEl.dataset.tbSwiperInited = '1';
        });
      });
    }

    initProductivitySwiper() {
      let prodSwiperEls = Array.from(this.querySelectorAll('.tb-productivity-swiper'));
      if (!prodSwiperEls.length) {
        const globalCandidates = Array.from(document.querySelectorAll('.tb-productivity-swiper'));
        prodSwiperEls = globalCandidates.filter((el) => {
          const host = el.closest('thunderbolt-formal-section');
          return !host || host === this || (this.id && host.id === this.id);
        });
      }
      if (!prodSwiperEls.length) {
        return;
      }

      this.runWhenSwiperReady(() => {
        prodSwiperEls.forEach((prodSwiperEl) => {
          if (prodSwiperEl.dataset.tbSwiperInited === '1') {
            return;
          }

          const containerEl = prodSwiperEl.closest('.tb-productivity-container') || this;
          const descEl = containerEl.querySelector('.tb-productivity-desc');
          const paginationEl = containerEl.querySelector('.tb-productivity-pagination');
          const slides = prodSwiperEl.querySelectorAll('.swiper-slide');
          if (!slides.length || !paginationEl) {
            return;
          }

          const prodSwiper = new Swiper(prodSwiperEl, {
            slidesPerView: 'auto',
            centeredSlides: true,
            spaceBetween: 8,
            loop: false,
            rewind: true,
            observer: true,
            observeParents: true,
            speed: 600,
            autoplay: {
              delay: 8000,
              disableOnInteraction: false
            },
            pagination: {
              el: paginationEl,
              clickable: true,
              renderBullet: function(index, className) {
                const slide = slides[index];
                const tabName = slide ? (slide.getAttribute('data-tabname') || '') : '';
                return '<span class="' + className + '">' + tabName + '</span>';
              }
            },
            breakpoints: {
              768: {
                spaceBetween: 40
              }
            },
            on: {
              init: function() {
                const activeSlide = this.slides[this.activeIndex];
                if (descEl && activeSlide) {
                  descEl.textContent = activeSlide.getAttribute('data-desc') || '';
                }
              },
              slideChange: function() {
                const activeSlide = this.slides[this.activeIndex];
                if (descEl && activeSlide) {
                  descEl.textContent = activeSlide.getAttribute('data-desc') || '';
                }
              }
            }
          });

          prodSwiperEl.dataset.tbSwiperInited = '1';
        });
      });
    }

    initStickyBar() {
      const stickyBar = this.querySelector('.tb-sticky-bar');
      const banner = this.querySelector('.revodok-banner');
      if (!stickyBar || !banner) return;

      let ticking = false;
      const updateStickyBarPosition = () => {
        const footer = document.querySelector('footer') || document.querySelector('#shopify-section-footer') || document.querySelector('.t4s-footer');
        if (!footer) {
          stickyBar.style.bottom = '0px';
        } else {
          const footerRect = footer.getBoundingClientRect();
          const overlap = window.innerHeight - footerRect.top;
          stickyBar.style.bottom = (overlap > 0 ? overlap : 0) + 'px';
        }

        const bannerRect = banner.getBoundingClientRect();
        stickyBar.classList.toggle('is-visible', bannerRect.bottom < 0);
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(updateStickyBarPosition);
          ticking = true;
        }
      }, { passive: true });
      window.addEventListener('resize', updateStickyBarPosition, { passive: true });
      updateStickyBarPosition();
    }

    normalizeTargetKey(rawTarget) {
      if (!rawTarget) return '';
      const cleaned = String(rawTarget).trim();
      if (!cleaned) return '';
      if (cleaned.startsWith('#')) return cleaned.slice(1);
      return cleaned;
    }

    resolveScrollTarget(rawTarget) {
      const targetKey = this.normalizeTargetKey(rawTarget);
      if (!targetKey) return null;

      const byId = document.getElementById(targetKey);
      if (byId) return byId;

      const byAnchorAttr = this.querySelector('[data-anchor="' + targetKey + '"]') || document.querySelector('[data-anchor="' + targetKey + '"]');
      if (byAnchorAttr) return byAnchorAttr;

      const aliasMap = {
        'about-thunderbolt': ['.tb-productivity-section'],
        highlights: ['.tb-compatibility-section', '.tb-media-voices-section'],
        specs: ['.tb-specs-section'],
        faq: ['.tb-faq-section'],
        'breathing-light': ['.tb-breathing-light-section'],
        'breathing-light-showcase': ['.tb-breathing-light-section'],
        'media-voices': ['.tb-media-voices-section'],
        'flagship-series': ['.tb-flagship-section']
      };

      const aliases = aliasMap[targetKey] || [];
      for (let i = 0; i < aliases.length; i += 1) {
        const targetEl = this.querySelector(aliases[i]) || document.querySelector(aliases[i]);
        if (targetEl) return targetEl;
      }

      return null;
    }

    getTopOverlayBottom(excludeElement) {
      const selectors = [
        '.t4s-section-header',
        '#shopify-section-header',
        '.header',
        '.t4s-announcement-bar',
        '.announcement-bar'
      ];
      let overlayBottom = 0;
      selectors.forEach((selector) => {
        const nodes = document.querySelectorAll(selector);
        nodes.forEach((node) => {
          if (!node || node === excludeElement || (excludeElement && node.contains(excludeElement))) return;
          const style = window.getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden') return;
          if (style.position !== 'fixed' && style.position !== 'sticky') return;
          const rect = node.getBoundingClientRect();
          if (rect.height <= 0 || rect.bottom <= 0) return;
          if (rect.top > 2) return;
          if (rect.bottom > overlayBottom) overlayBottom = rect.bottom;
        });
      });
      return overlayBottom;
    }

    getScrollOffset() {
      const stickyNav = this.querySelector('#tb-sticky-nav');
      const stickyHeight = stickyNav ? (stickyNav.offsetHeight || 0) : 0;
      const overlayBottom = this.getTopOverlayBottom(stickyNav || null);
      return overlayBottom + stickyHeight + 6;
    }

    smoothScrollToTarget(rawTarget) {
      const targetEl = this.resolveScrollTarget(rawTarget);
      if (!targetEl) return false;
      const offset = this.getScrollOffset();
      const targetPos = targetEl.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, targetPos), behavior: 'smooth' });
      return true;
    }

    initSmoothScroll() {
      const smoothLinks = this.querySelectorAll('a[href^="#"], a[data-target], a[data-scroll-target]');
      if (!smoothLinks.length) return;

      smoothLinks.forEach((link) => {
        if (link.dataset.tbSmoothBound === '1') return;
        link.dataset.tbSmoothBound = '1';
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href') || '';
          const explicitTarget = link.getAttribute('data-scroll-target') || link.getAttribute('data-target');
          let rawTarget = explicitTarget;
          if (!rawTarget && href.startsWith('#')) {
            rawTarget = href;
          }
          if (!rawTarget && link.classList.contains('tb-flagship-explore')) {
            rawTarget = 'breathing-light';
          }
          if (!rawTarget) return;
          const didScroll = this.smoothScrollToTarget(rawTarget);
          if (!didScroll) return;
          e.preventDefault();
        });
      });
    }

    initStickyNav() {
      const stickyNav = this.querySelector('#tb-sticky-nav');
      if (!stickyNav) return;
      if (stickyNav.dataset.tbNavFullyBound === '1') {
        return;
      }
      stickyNav.dataset.tbNavFullyBound = '1';

      const links = stickyNav.querySelectorAll('.tb-sticky-nav-link');
      const navToggle = stickyNav.querySelector('.tb-sticky-nav-toggle');
      const closeMobileDropdown = () => {
        stickyNav.classList.remove('is-open');
        if (navToggle) navToggle.setAttribute('aria-expanded', 'false');
      };
      const toggleMobileDropdown = () => {
        const isOpen = stickyNav.classList.toggle('is-open');
        if (navToggle) navToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      };

      if (navToggle && navToggle.dataset.tbNavToggleBound !== '1') {
        navToggle.dataset.tbNavToggleBound = '1';
        navToggle.addEventListener('click', (event) => {
          if (window.innerWidth > 1023.98) return;
          event.preventDefault();
          toggleMobileDropdown();
        });
      }

      links.forEach((link) => {
        if (link.dataset.tbNavLinkBound === '1') return;
        link.dataset.tbNavLinkBound = '1';
        link.addEventListener('click', () => {
          if (window.innerWidth > 1023.98) return;
          closeMobileDropdown();
        });
      });

      const closeIfOutside = (event) => {
        if (window.innerWidth > 1023.98) return;
        const target = event.target;
        if (!target || stickyNav.contains(target)) return;
        closeMobileDropdown();
      };
      document.addEventListener('click', closeIfOutside);
      document.addEventListener('pointerdown', closeIfOutside, true);
      window.addEventListener(
        'resize',
        () => {
          if (window.innerWidth > 1023.98) {
            closeMobileDropdown();
          }
        },
        { passive: true }
      );

      const sections = [];
      links.forEach((link) => {
        const targetId = link.getAttribute('data-target');
        const targetEl = this.resolveScrollTarget(targetId);
        if (targetEl) sections.push({ element: targetEl, link: link });
      });
      const lastSectionEl = sections.length ? sections[sections.length - 1].element : null;
      const faqBoundaryEl = this.querySelector('.tb-faq-section') || document.querySelector('.tb-faq-section');

      const updateActiveLink = () => {
        const viewportOffset = this.getScrollOffset();
        let currentSection = null;
        sections.forEach((section) => {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= viewportOffset) currentSection = section;
        });
        links.forEach((link) => link.classList.remove('active'));
        if (currentSection) currentSection.link.classList.add('active');
        const hideBoundaryEl = faqBoundaryEl || lastSectionEl;
        if (hideBoundaryEl) {
          const lastRect = hideBoundaryEl.getBoundingClientRect();
          const lastBottomAbs = lastRect.bottom + window.pageYOffset;
          const shouldHideNav = window.pageYOffset + viewportOffset >= lastBottomAbs;
          stickyNav.classList.toggle('is-nav-hidden', shouldHideNav);
        } else {
          stickyNav.classList.remove('is-nav-hidden');
        }
      };

      window.addEventListener('scroll', updateActiveLink, { passive: true });
      window.addEventListener('resize', updateActiveLink, { passive: true });
      updateActiveLink();
    }

    initTabs() {
      const sections = this.querySelectorAll('.tb-compatibility-section');
      if (!sections.length) return;

      sections.forEach((section) => {
        const tabsWrap = section.querySelector('.tb-compatibility-tabs');
        const tabs = section.querySelectorAll('.tb-compatibility-tab');
        const panes = section.querySelectorAll('.tb-compatibility-pane');
        const descEl = section.querySelector('.tb-compatibility-desc');
        if (!tabs.length || !panes.length || !tabsWrap) return;

        let slider = tabsWrap.querySelector('.tb-compatibility-tab-slider');
        if (!slider) {
          slider = document.createElement('div');
          slider.className = 'tb-compatibility-tab-slider';
          tabsWrap.insertBefore(slider, tabsWrap.firstChild);
        }

        const moveSlider = (activeTab) => {
          if (!activeTab) return;
          slider.style.width = activeTab.offsetWidth + 'px';
          slider.style.transform = 'translateX(' + activeTab.offsetLeft + 'px)';
        };

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const target = tab.getAttribute('data-tab');
            tabs.forEach((item) => item.classList.remove('active'));
            panes.forEach((item) => item.classList.remove('active'));
            tab.classList.add('active');
            const targetPane = section.querySelector('.tb-compatibility-pane[data-pane="' + target + '"]');
            if (targetPane) targetPane.classList.add('active');
            if (descEl && targetPane) {
              descEl.innerHTML = targetPane.getAttribute('data-desc') || '';
            }
            moveSlider(tab);
          });
        });

        const activeTab = section.querySelector('.tb-compatibility-tab.active') || tabs[0];
        const activePane = section.querySelector('.tb-compatibility-pane.active') || panes[0];
        if (descEl && activePane) {
          descEl.innerHTML = activePane.getAttribute('data-desc') || '';
        }
        moveSlider(activeTab);
        window.addEventListener('resize', () => moveSlider(section.querySelector('.tb-compatibility-tab.active') || tabs[0]), { passive: true });
      });
    }

    initBreathingLightShowcase() {
      const localSections = Array.from(this.querySelectorAll('.tb-breathing-light-section'));
      let sections = localSections;
      if (!sections.length) {
        const globalSections = Array.from(document.querySelectorAll('.tb-breathing-light-section'));
        sections = globalSections;
      }
      if (!sections.length) {
        this.__tbBreathingRetryCount = (this.__tbBreathingRetryCount || 0) + 1;
        if (this.__tbBreathingRetryCount <= 3) {
          window.setTimeout(() => this.initBreathingLightShowcase(), 400);
        }
        return;
      }
      this.__tbBreathingRetryCount = 0;

      sections.forEach((section) => {
        if (section.dataset.tbBlInited === '1') return;
        const stage = section.querySelector('.tb-breathing-light-stage');
        const hotspotsLayer = section.querySelector('.tb-breathing-light-hotspots');
        const cardsWrap = section.querySelector('.tb-breathing-light-cards');
        const hotspots = Array.from(section.querySelectorAll('.tb-breathing-light-hotspot'));
        const cards = Array.from(section.querySelectorAll('.tb-breathing-light-card'));
        const mobileModal = section.querySelector('.tb-breathing-light-mobile-modal');
        const mobileModalBackdrop = section.querySelector('.tb-breathing-light-mobile-modal-backdrop');
        const mobileModalPanel = section.querySelector('.tb-breathing-light-mobile-modal-panel');
        const mobileModalClose = section.querySelector('.tb-breathing-light-mobile-modal-close');
        const mobileModalCardsWrap = section.querySelector('.tb-breathing-light-mobile-modal-cards');
        const mobilePrevBtn = section.querySelector('.tb-breathing-light-mobile-prev');
        const mobileNextBtn = section.querySelector('.tb-breathing-light-mobile-next');
        const mobileProgressFill = section.querySelector('.tb-breathing-light-mobile-progress-fill');
        let lastModalOpenAt = 0;
        const orderedIndexes = cards
          .map((card) => card.getAttribute('data-bl-index'))
          .filter(Boolean);
        if (mobileModalCardsWrap && !mobileModalCardsWrap.children.length) {
          const mobileTemplates = Array.from(section.querySelectorAll('.tb-breathing-light-mobile-template'));
          if (mobileTemplates.length > 0) {
            mobileTemplates.forEach((tmpl) => {
              const clone = tmpl.cloneNode(true);
              clone.classList.remove('tb-breathing-light-mobile-template');
              clone.classList.add('tb-breathing-light-mobile-card');
              clone.style.display = ''; 
              mobileModalCardsWrap.appendChild(clone);
            });
          } else {
            cards.forEach((card) => {
              const clone = card.cloneNode(true);
              clone.classList.remove('tb-breathing-light-card');
              clone.classList.remove('active');
              clone.classList.add('tb-breathing-light-mobile-card');
              mobileModalCardsWrap.appendChild(clone);
            });
          }
        }
        const mobileCards = Array.from(section.querySelectorAll('.tb-breathing-light-mobile-card'));
        const isMobileView = () => window.matchMedia('(max-width: 1023.98px)').matches;
        if (!hotspots.length || !cards.length || !stage || !cardsWrap || !hotspotsLayer) {
          return;
        }
        let lastHoverIndex = '';
        let hideTimer = null;
        const hideDelay = 180;
        let touchStartX = 0;
        let touchStartY = 0;
        let touchActive = false;

        const updateMobileControls = (index) => {
          if (!orderedIndexes.length) return;
          const activePos = orderedIndexes.indexOf(index);
          if (activePos === -1) return;
          if (mobilePrevBtn) mobilePrevBtn.classList.toggle('swiper-button-disabled', activePos <= 0);
          if (mobileNextBtn) mobileNextBtn.classList.toggle('swiper-button-disabled', activePos >= orderedIndexes.length - 1);
          if (mobileProgressFill) {
            const progress = orderedIndexes.length <= 1 ? 1 : (activePos + 1) / orderedIndexes.length;
            mobileProgressFill.style.width = (progress * 100) + '%';
          }
        };

        const placeCardNearHotspot = (hotspot, activeCard) => {
          if (!hotspot || !activeCard) return;
          if (window.matchMedia('(max-width: 1023.98px)').matches) {
            cardsWrap.style.left = '';
            cardsWrap.style.top = '';
            return;
          }

          const stageRect = stage.getBoundingClientRect();
          const hotspotRect = hotspot.getBoundingClientRect();
          const gap = 16;
          const wrapWidth = cardsWrap.offsetWidth;
          const cardHeight = activeCard.offsetHeight;

          let left = hotspotRect.left - stageRect.left + hotspotRect.width + gap;
          let top = hotspotRect.top - stageRect.top + (hotspotRect.height / 2) - (cardHeight / 2);

          const maxLeft = Math.max(0, stageRect.width - wrapWidth);
          if (left > maxLeft) {
            left = hotspotRect.left - stageRect.left - wrapWidth - gap;
          }
          left = Math.max(0, Math.min(left, maxLeft));

          const maxTop = Math.max(0, stageRect.height - cardHeight);
          top = Math.max(0, Math.min(top, maxTop));

          cardsWrap.style.left = left + 'px';
          cardsWrap.style.top = top + 'px';
        };

        const activate = (index, sourceHotspot) => {
          hotspots.forEach((hotspot) => {
            hotspot.classList.toggle('active', hotspot.getAttribute('data-bl-index') === index);
          });
          if (isMobileView() && mobileModal) {
            mobileCards.forEach((card) => {
              card.classList.toggle('active', card.getAttribute('data-bl-index') === index);
            });
            section.classList.add('is-mobile-card-open');
            mobileModal.classList.add('active');
            mobileModal.setAttribute('aria-hidden', 'false');
            document.body.classList.add('tb-no-scroll');
            lastModalOpenAt = Date.now();
            updateMobileControls(index);
            return;
          }
          let activeCard = null;
          cards.forEach((card) => {
            const matched = card.getAttribute('data-bl-index') === index;
            card.classList.toggle('active', matched);
            if (matched) activeCard = card;
          });
          if (activeCard) {
            cardsWrap.style.height = activeCard.offsetHeight + 'px';
          }
          placeCardNearHotspot(sourceHotspot || section.querySelector('.tb-breathing-light-hotspot[data-bl-index="' + index + '"]'), activeCard);
          cardsWrap.classList.add('active');
        };

        const clearActive = () => {
          hotspots.forEach((hotspot) => hotspot.classList.remove('active'));
          mobileCards.forEach((card) => {
            card.classList.remove('active');
          });
          cards.forEach((card) => {
            card.classList.remove('active');
          });
          section.classList.remove('is-mobile-card-open');
          if (mobileModal) {
            mobileModal.classList.remove('active');
            mobileModal.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('tb-no-scroll');
          }
          cardsWrap.classList.remove('active');
          cardsWrap.style.height = '';
          if (mobileProgressFill) mobileProgressFill.style.width = '0';
          if (mobilePrevBtn) mobilePrevBtn.classList.remove('swiper-button-disabled');
          if (mobileNextBtn) mobileNextBtn.classList.remove('swiper-button-disabled');
          lastHoverIndex = '';
        };

        const cancelHide = () => {
          if (!hideTimer) return;
          window.clearTimeout(hideTimer);
          hideTimer = null;
          cardsWrap.classList.remove('is-hiding');
        };

        const scheduleHide = () => {
          if (isMobileView()) return;
          cancelHide();
          if (!cardsWrap.classList.contains('active')) return;
          cardsWrap.classList.add('is-hiding');
          hideTimer = window.setTimeout(() => {
            clearActive();
            cardsWrap.classList.remove('is-hiding');
            hideTimer = null;
          }, hideDelay);
        };

        const activateByHotspot = (hotspot) => {
          if (!hotspot) return;
          cancelHide();
          const index = hotspot.getAttribute('data-bl-index');
          if (index === lastHoverIndex && cardsWrap.classList.contains('active')) return;
          if (index !== lastHoverIndex) {
            lastHoverIndex = index;
          }
          activate(index, hotspot);
        };

        const findHotspotByClientPoint = (clientX, clientY) => {
          for (let i = 0; i < hotspots.length; i += 1) {
            const hotspot = hotspots[i];
            const rect = hotspot.getBoundingClientRect();
            if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
              return hotspot;
            }
          }
          return null;
        };

        const closestHotspot = (target) => {
          if (!target || typeof target.closest !== 'function') return null;
          return target.closest('.tb-breathing-light-hotspot');
        };

        const isInInteractiveArea = (target) => {
          if (!target) return false;
          return hotspotsLayer.contains(target) || cardsWrap.contains(target);
        };

        hotspotsLayer.addEventListener('pointerover', (event) => {
          cancelHide();
          const hotspot = closestHotspot(event.target);
          if (!hotspot || !hotspotsLayer.contains(hotspot)) {
            return;
          }
          activateByHotspot(hotspot);
        });

        hotspotsLayer.addEventListener('mousemove', (event) => {
          cancelHide();
          const hotspot = closestHotspot(event.target);
          if (!hotspot || !hotspotsLayer.contains(hotspot)) return;
          activateByHotspot(hotspot);
        });

        hotspotsLayer.addEventListener('mouseleave', (event) => {
          const toTarget = event.relatedTarget;
          if (isInInteractiveArea(toTarget)) return;
          scheduleHide();
        });

        hotspotsLayer.addEventListener('click', (event) => {
          cancelHide();
          const hotspot = closestHotspot(event.target) || findHotspotByClientPoint(event.clientX, event.clientY);
          if (!hotspot || !hotspotsLayer.contains(hotspot)) return;
          activateByHotspot(hotspot);
        });

        section.addEventListener('pointermove', (event) => {
          if (isMobileView()) return;
          if (cardsWrap.contains(event.target)) return;
          const hotspot = findHotspotByClientPoint(event.clientX, event.clientY);
          if (!hotspot) return;
          activateByHotspot(hotspot);
        }, { passive: true });

        section.addEventListener('click', (event) => {
          if (mobileModal && mobileModal.contains(event.target)) return;
          if (cardsWrap.contains(event.target)) return;
          const hotspot = closestHotspot(event.target) || findHotspotByClientPoint(event.clientX, event.clientY);
          if (!hotspot) return;
          activateByHotspot(hotspot);
        }, true);

        hotspots.forEach((hotspot) => {
          hotspot.addEventListener('pointerenter', () => activateByHotspot(hotspot));
          hotspot.addEventListener('mouseenter', () => activateByHotspot(hotspot));
          hotspot.addEventListener('mousemove', () => activateByHotspot(hotspot));
          hotspot.addEventListener('click', () => {
            activateByHotspot(hotspot);
          });
          hotspot.addEventListener('mouseleave', (event) => {
            const toTarget = event.relatedTarget;
            if (isInInteractiveArea(toTarget)) return;
            scheduleHide();
          });
        });

        stage.addEventListener('pointermove', (event) => {
          if (isMobileView()) return;
          const hotspot = closestHotspot(event.target);
          if (!hotspot || !stage.contains(hotspot)) return;
          activateByHotspot(hotspot);
        });

        stage.addEventListener('mousemove', (event) => {
          if (isMobileView()) return;
          const hotspot = closestHotspot(event.target);
          if (!hotspot || !stage.contains(hotspot)) return;
          activateByHotspot(hotspot);
        });

        stage.addEventListener('click', (event) => {
          cancelHide();
          if (cardsWrap.contains(event.target)) return;
          const hotspot = closestHotspot(event.target) || findHotspotByClientPoint(event.clientX, event.clientY);
          if (!hotspot || !stage.contains(hotspot)) {
            if (isMobileView() && mobileModal && mobileModal.classList.contains('active') && !mobileModal.contains(event.target)) {
              clearActive();
            }
            return;
          }
          activateByHotspot(hotspot);
        });

        cardsWrap.addEventListener('mouseenter', cancelHide);
        cardsWrap.addEventListener('mousemove', cancelHide);
        cardsWrap.addEventListener('mouseleave', (event) => {
          const toTarget = event.relatedTarget;
          if (isInInteractiveArea(toTarget)) return;
          scheduleHide();
        });

        const mobileTouchTarget = mobileModalPanel || cardsWrap;
        mobileTouchTarget.addEventListener('touchstart', (event) => {
          if (!isMobileView()) return;
          if ((!mobileModal || !mobileModal.classList.contains('active')) || !event.touches.length) return;
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
          touchActive = true;
        }, { passive: true });

        mobileTouchTarget.addEventListener('touchend', (event) => {
          if (!isMobileView()) return;
          if (!touchActive || !event.changedTouches.length) return;
          const endX = event.changedTouches[0].clientX;
          const endY = event.changedTouches[0].clientY;
          const deltaX = endX - touchStartX;
          const deltaY = endY - touchStartY;
          touchActive = false;
          if (Math.abs(deltaX) < 40 || Math.abs(deltaY) > Math.abs(deltaX)) return;

          const activeIndex = mobileModal?.querySelector('.tb-breathing-light-mobile-card.active')?.getAttribute('data-bl-index') || orderedIndexes[0];
          const activePos = orderedIndexes.indexOf(activeIndex);
          if (activePos === -1) return;
          const targetPos = deltaX < 0 ? activePos + 1 : activePos - 1;
          if (targetPos < 0 || targetPos >= orderedIndexes.length) return;
          const targetIndex = orderedIndexes[targetPos];
          const targetHotspot = section.querySelector('.tb-breathing-light-hotspot[data-bl-index="' + targetIndex + '"]');
          activate(targetIndex, targetHotspot);
        }, { passive: true });

        if (mobilePrevBtn) {
          mobilePrevBtn.addEventListener('click', () => {
            const activeIndex = mobileModal?.querySelector('.tb-breathing-light-mobile-card.active')?.getAttribute('data-bl-index') || orderedIndexes[0];
            const activePos = orderedIndexes.indexOf(activeIndex);
            if (activePos <= 0) return;
            const targetIndex = orderedIndexes[activePos - 1];
            const targetHotspot = section.querySelector('.tb-breathing-light-hotspot[data-bl-index="' + targetIndex + '"]');
            activate(targetIndex, targetHotspot);
          });
        }
        if (mobileNextBtn) {
          mobileNextBtn.addEventListener('click', () => {
            const activeIndex = mobileModal?.querySelector('.tb-breathing-light-mobile-card.active')?.getAttribute('data-bl-index') || orderedIndexes[0];
            const activePos = orderedIndexes.indexOf(activeIndex);
            if (activePos === -1 || activePos >= orderedIndexes.length - 1) return;
            const targetIndex = orderedIndexes[activePos + 1];
            const targetHotspot = section.querySelector('.tb-breathing-light-hotspot[data-bl-index="' + targetIndex + '"]');
            activate(targetIndex, targetHotspot);
          });
        }
        if (mobileModalBackdrop) {
          const onBackdropClose = () => {
            if (Date.now() - lastModalOpenAt < 250) return;
            clearActive();
          };
          mobileModalBackdrop.addEventListener('click', onBackdropClose);
          mobileModalBackdrop.addEventListener('pointerdown', onBackdropClose, { passive: true });
        }
        if (mobileModalClose) {
          mobileModalClose.addEventListener('click', (e) => {
            if (Date.now() - lastModalOpenAt < 250) return;
            clearActive();
          });
        }

        stage.addEventListener('mouseleave', scheduleHide);
        clearActive();
        section.dataset.tbBlInited = '1';
      });
    }

    initFaq() {
      const localSections = Array.from(this.querySelectorAll('.tb-faq-section'));
      let sections = localSections;
      if (!sections.length) {
        sections = Array.from(document.querySelectorAll('.tb-faq-section'));
      }
      if (!sections.length) {
        this.__tbFaqRetryCount = (this.__tbFaqRetryCount || 0) + 1;
        if (this.__tbFaqRetryCount <= 3) {
          window.setTimeout(() => this.initFaq(), 400);
        }
        return;
      }
      this.__tbFaqRetryCount = 0;

      sections.forEach((section) => {
        if (section.dataset.tbFaqInited === '1') return;
        const items = Array.from(section.querySelectorAll('.tb-faq-item'));
        if (!items.length) return;

        const getAnswer = (item) => item.querySelector('.tb-faq-answer');

        const expandAnswer = (item, answer) => {
          item.classList.add('active');
          answer.style.display = 'block';
          answer.style.height = '0px';
          void answer.offsetHeight;
          const targetHeight = answer.scrollHeight;
          window.requestAnimationFrame(() => {
            answer.style.height = targetHeight + 'px';
          });
          const onEnd = (event) => {
            if (event.propertyName !== 'height') return;
            answer.style.height = 'auto';
            answer.removeEventListener('transitionend', onEnd);
          };
          answer.addEventListener('transitionend', onEnd);
        };

        const collapseAnswer = (item, answer) => {
          const fromHeight = answer.scrollHeight;
          answer.style.height = fromHeight + 'px';
          void answer.offsetHeight;
          item.classList.remove('active');
          window.requestAnimationFrame(() => {
            answer.style.height = '0px';
          });
          const onEnd = (event) => {
            if (event.propertyName !== 'height') return;
            answer.removeEventListener('transitionend', onEnd);
          };
          answer.addEventListener('transitionend', onEnd);
        };

        const setItemState = (item, expanded) => {
          const answer = getAnswer(item);
          if (!answer) {
            item.classList.toggle('active', expanded);
            return;
          }
          if (expanded) expandAnswer(item, answer);
          else collapseAnswer(item, answer);
        };

        const openItem = (targetItem, allowCollapseCurrent) => {
          const targetIsActive = targetItem.classList.contains('active');
          const shouldExpandTarget = allowCollapseCurrent ? !targetIsActive : true;
          items.forEach((item) => {
            if (item !== targetItem && item.classList.contains('active')) {
              setItemState(item, false);
            }
          });
          if (shouldExpandTarget) {
            window.requestAnimationFrame(() => {
              setItemState(targetItem, true);
            });
          } else {
            setItemState(targetItem, false);
          }
        };

        items.forEach((item) => {
          const answer = getAnswer(item);
          if (!answer) return;
          answer.style.transition = 'height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          answer.style.overflow = 'hidden';
          const isActive = item.classList.contains('active');
          if (!isActive) {
            answer.style.display = 'block';
            answer.style.height = '0px';
          } else {
            answer.style.display = 'block';
            answer.style.height = 'auto';
          }
        });

        const firstItem = items.find((item) => item.classList.contains('active')) || items[0];
        if (firstItem) openItem(firstItem, false);

        items.forEach((item) => {
          const question = item.querySelector('.tb-faq-question');
          if (!question) return;
          question.addEventListener('click', () => {
            openItem(item, true);
          });
        });

        section.dataset.tbFaqInited = '1';
      });
    }

    initVideoModal() {
      const triggers = this.querySelectorAll('.js-tb-video-trigger');
      const modal = this.querySelector('#tb-video-modal');
      if (!triggers.length || !modal) return;
      if (modal.dataset.tbVideoModalBound === '1') {
        return;
      }
      modal.dataset.tbVideoModalBound = '1';

      const closeBtn = modal.querySelector('.tb-video-close');
      const overlay = modal.querySelector('.tb-video-modal-overlay');
      const iframe = modal.querySelector('#tb-video-iframe');

      const closeModal = () => {
        modal.classList.remove('is-visible');
        if (iframe) iframe.src = '';
        document.body.style.overflow = '';
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const videoUrl = trigger.getAttribute('data-video-url');
          if (!videoUrl || !iframe) return;
          iframe.src = videoUrl;
          modal.classList.add('is-visible');
          document.body.style.overflow = 'hidden';
        });
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', closeModal);
      }
      if (overlay) {
        overlay.addEventListener('click', closeModal);
        overlay.addEventListener('pointerdown', closeModal, { passive: true });
      }
    }

    initSpecsMobile() {
      const mobile = this.querySelector('.tb-specs-mobile');
      if (!mobile) return;

      const dataNodes = Array.from(mobile.querySelectorAll('.tb-specs-mobile-data'));
      const products = {};
      dataNodes.forEach((node) => {
        const id = node.getAttribute('data-id');
        products[id] = {
          title: node.getAttribute('data-title') || '',
          shortname: node.getAttribute('data-shortname') || '',
          url: node.getAttribute('data-url') || '#',
          image: node.getAttribute('data-image') || '',
          specs: (function() {
            try { return JSON.parse(node.getAttribute('data-specs') || '{}'); }
            catch (e) { return {}; }
          })()
        };
      });

      const selects = Array.from(mobile.querySelectorAll('.tb-specs-mobile-select'));
      const rows = Array.from(mobile.querySelectorAll('.tb-specs-mobile-row'));

      const updateSelectOptions = () => {
        if (selects.length < 2) return;
        const val0 = selects[0].value;
        const val1 = selects[1].value;
        
        Array.from(selects[0].options).forEach(opt => {
          opt.disabled = (opt.value === val1);
        });
        
        Array.from(selects[1].options).forEach(opt => {
          opt.disabled = (opt.value === val0);
        });
      };

      const renderSlot = (slotIndex, productId) => {
        const product = products[productId];
        if (!product) return;
        const card = mobile.querySelector('.tb-specs-mobile-product[data-slot="' + slotIndex + '"]');
        if (card) {
          const img = card.querySelector('img');
          const nameEl = card.querySelector('.tb-specs-mobile-name');
          const buyEl = card.querySelector('.tb-specs-mobile-buynow');
          if (img) img.src = product.image || '';
          if (nameEl) nameEl.textContent = product.shortname || product.title || '';
          if (buyEl) buyEl.href = product.url || '#';
        }
        rows.forEach((row) => {
          const key = row.getAttribute('data-key');
          const cell = row.querySelector('.tb-specs-mobile-value[data-slot="' + slotIndex + '"]');
          if (cell) {
            cell.innerHTML = (product.specs && product.specs[key]) ? String(product.specs[key]).replace(/\\n/g, '<br>') : '/';
          }
        });
      };

      if (selects.length >= 2 && dataNodes.length >= 2) {
        const id0 = dataNodes[0].getAttribute('data-id');
        const id1 = dataNodes[1].getAttribute('data-id');
        if (id0) { selects[0].value = id0; renderSlot(0, id0); }
        if (id1) { selects[1].value = id1; renderSlot(1, id1); }
        updateSelectOptions();
      }

      selects.forEach((select) => {
        select.addEventListener('change', () => {
          const slot = select.getAttribute('data-slot');
          renderSlot(slot, select.value);
          updateSelectOptions();
        });
      });
    }

    initUltraVideo() {
      const video = this.querySelector('.tb-ultra-video');
      if (!video) return;

      const setSource = () => {
        const isMobile = window.innerWidth <= 768;
        const pcSrc = video.getAttribute('data-pc-src');
        const mbSrc = video.getAttribute('data-mb-src');
        const newSrc = (isMobile && mbSrc) ? mbSrc : pcSrc;
        if (video.src !== newSrc) {
          video.src = newSrc;
          video.load();
        }
      };

      setSource();
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(setSource, 200);
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
            observer.unobserve(video);
          }
        });
      }, { threshold: 0.5 });
      observer.observe(video);
    }

    initZoomVideoShowcase() {
      const sections = this.querySelectorAll('.tb-zoom-video-section');
      if (!sections.length) return;

      sections.forEach((section) => {
        const video = section.querySelector('.tb-zoom-video');
        if (!video) return;
        let hasUserScrolled = window.pageYOffset > 0;
        let played = false;
        let observer;

        const setSource = () => {
          const isMobile = window.innerWidth <= 768;
          const pcSrc = video.getAttribute('data-pc-src') || '';
          const mbSrc = video.getAttribute('data-mb-src') || '';
          const pcPoster = video.getAttribute('data-poster-pc') || '';
          const mbPoster = video.getAttribute('data-poster-mb') || '';
          const targetSrc = isMobile ? (mbSrc || pcSrc) : (pcSrc || mbSrc);
          const targetPoster = isMobile ? (mbPoster || pcPoster) : (pcPoster || mbPoster);
          if (targetPoster) video.poster = targetPoster;
          if (targetSrc && video.getAttribute('src') !== targetSrc) {
            video.setAttribute('src', targetSrc);
            video.load();
          }
        };

        setSource();
        window.addEventListener('resize', setSource, { passive: true });

        const playOnce = () => {
          if (played || !hasUserScrolled) return;
          const rect = section.getBoundingClientRect();
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
          const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
          const minBase = Math.max(1, Math.min(rect.height || 1, viewportHeight || 1));
          if ((visibleHeight / minBase) < 0.35) return;
          played = true;
          video.currentTime = 0;
          video.play().catch(() => {});
          if (observer) observer.unobserve(section);
          window.removeEventListener('scroll', onUserScroll);
        };

        const onUserScroll = () => {
          hasUserScrolled = true;
          playOnce();
        };

        window.addEventListener('scroll', onUserScroll, { passive: true });
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            playOnce();
          });
        }, { threshold: 0.35 });
        observer.observe(section);
      });
    }

    initEmailSignUp() {
      let emailBlocks = this.querySelectorAll('.thunderbolt-email-block');
      if (!emailBlocks.length) {
        emailBlocks = document.querySelectorAll('.thunderbolt-email-block');
      }
      if (!emailBlocks.length) return;

      emailBlocks.forEach((block) => {
        if (block.dataset.tbEmailInited === '1') {
           return;
        }
        block.dataset.tbEmailInited = '1';

        const submitBtn = block.querySelector('.thunderbolt-email-submit');
        const input = block.querySelector('.thunderbolt-email-input');
        
        const privacyCheckbox = block.querySelector('.privacy-checkbox');
        const errorMsg = block.querySelector('.help-block .error');
        const successMsg = block.querySelector('.help-block .message');

        const formId = block.getAttribute('data-form-id') || '';
        const successText = block.getAttribute('data-success-text') || 'Thanks for subscribing!';
        const failText = block.getAttribute('data-fail-text') || 'Subscription failed, please try again later.';
        const privacyError = block.getAttribute('data-privacy-error') || 'Please agree to the privacy policy.';
        const emailError = block.getAttribute('data-email-error') || 'Please enter a valid email address.';

        if (!submitBtn || !input) return;

        const validateEmail = (email) => {
          return String(email).toLowerCase().match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          );
        };

        const showError = (msg) => {
          if (errorMsg) {
            errorMsg.textContent = msg;
            errorMsg.style.display = 'block';
          }
          if (successMsg) successMsg.style.display = 'none';
        };

        const showSuccess = (msg) => {
          if (successMsg) {
            successMsg.textContent = msg;
            successMsg.style.display = 'block';
          }
          if (errorMsg) errorMsg.style.display = 'none';
        };

        submitBtn.addEventListener('click', () => {
          const emailVal = input.value.trim();

          if (!validateEmail(emailVal)) {
            showError(emailError);
            return;
          }

          if (privacyCheckbox && !privacyCheckbox.checked) {
            showError(privacyError);
            return;
          }

          // Clear previous messages
          if (errorMsg) errorMsg.style.display = 'none';
          if (successMsg) successMsg.style.display = 'none';

          if (submitBtn.classList.contains('loading')) {
            return;
          }

          submitBtn.classList.add('loading');
          fetch('https://ecf.cirkleinc.com/api/form-submit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: emailVal,
              accepts_marketing: privacyCheckbox && privacyCheckbox.checked ? 'on' : 'off',
              form_id: formId
            })
          })
          .then(response => {
            return response.json();
          })
          .then(data => {
            const isSuccess = data && data.status === 'success';
            if (isSuccess) {
              input.value = '';
              showSuccess(successText);
            } else {
              const serverMsg = (data && data.errors && data.errors.email && data.errors.email[0]) || (data && data.message) || failText;
              showError(serverMsg);
            }
            if (window.ugGa4Track && typeof window.ugGa4Track.subscriptionEvent === 'function') {
              try {
                window.ugGa4Track.subscriptionEvent({ isSuccess: !!isSuccess, position: 'thunderbolt-5' });
              } catch (_) {}
            }
            submitBtn.classList.remove('loading');
          })
          .catch(error => {
             showError(failText);
             if (window.ugGa4Track && typeof window.ugGa4Track.subscriptionEvent === 'function') {
               try {
                 window.ugGa4Track.subscriptionEvent({ isSuccess: false, position: 'thunderbolt-5' });
               } catch (_) {}
             }
             submitBtn.classList.remove('loading');
          });
        });

        if (input) {
          input.addEventListener('focus', function() {
            const priv = block.querySelector('.thunderbolt-email-privacy');
            if (priv && priv.classList.contains('dpn_impotant')) {
              priv.classList.remove('dpn_impotant');
            }
          });
        }

        // Allow pressing Enter in the input field
        input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submitBtn.click();
          }
        });
      });
    }

    initAnimations() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
      gsap.registerPlugin(ScrollTrigger);

      const titleFromVars = {
        autoAlpha: 1,
        y: 34,
        scale: 0.985,
        backgroundPosition: '0% 50%'
      };
      const titleToVars = {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        backgroundPosition: '100% 50%',
        immediateRender: false,
        duration: 1.02,
        ease: 'power4.out',
        stagger: 0.1
      };
      const subtitleFromVars = {
        autoAlpha: 1,
        y: 26,
        scale: 0.992,
        backgroundPosition: '10% 50%'
      };
      const subtitleToVars = {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        backgroundPosition: '100% 50%',
        immediateRender: false,
        duration: 0.88,
        ease: 'power3.out',
        stagger: 0.07
      };

      const sectionAnimConfigs = [
        { section: '.revodok-banner', title: '.revodok-banner-title', subtitle: '.revodok-banner-subtitle, .revodok-banner-description' },
        { section: '.revodok-email', title: '.revodok-email-title', subtitle: '.revodok-email-subtitle' },
        { section: '.tb-productivity-section', title: '.tb-productivity-title', subtitle: '' },
        { section: '.tb-ext-section', title: '.tb-ext-title', subtitle: '' },
        { section: '.tb-ultra-section', title: '.tb-ultra-title', subtitle: '.tb-ultra-subtitle' },
        { section: '.tb-creativity-section', title: '.tb-creativity-title', subtitle: '.tb-creativity-desc' },
        { section: '.tb-new-standard-section, .tb-ns-section', title: '.tb-ns-title', subtitle: '' },
        { section: '.tb-workstation-section, .tb-ws-section', title: '.tb-ws-title', subtitle: '.tb-ws-subtitle' },
        { section: '.tb-charging-section', title: '.tb-charging-title', subtitle: '' },
        { section: '.tb-feature-row-section', title: '.tb-feature-row-title', subtitle: '' },
        { section: '.tb-compatibility-section', title: '.tb-compatibility-title', subtitle: '' },
        { section: '.tb-outputs-section', title: '.tb-outputs-title', subtitle: '' },
        { section: '.tb-specs-section', title: '.tb-specs-title', subtitle: '' },
        { section: '.tb-flagship-section', title: '.tb-flagship-title', subtitle: '' },
        { section: '.tb-media-voices-section', title: '.tb-mv-title', subtitle: '.tb-mv-desc' },
        { section: '.tb-products', title: '.tb-products-title', subtitle: '' },
        { section: '.tb-zoom-video-section', title: '.tb-zoom-video-title', subtitle: '' },
        { section: '.tb-breathing-light-section', title: '.tb-breathing-light-title', subtitle: '' },
        { section: '.tb-faq-section', title: '.tb-faq-title', subtitle: '' }
      ];
      sectionAnimConfigs.forEach((config) => {
        const sections = this.querySelectorAll(config.section);
        sections.forEach((section) => {
          if (section.dataset.tbTitleAnimInited === '1') return;
          const titleEls = Array.from(section.querySelectorAll(config.title)).filter((el) => !!el && !el.closest('.swiper-slide') && !el.closest('.tb-breathing-light-card'));
          const subtitleEls = config.subtitle
            ? Array.from(section.querySelectorAll(config.subtitle)).filter((el) => !!el && !el.closest('.swiper-slide') && !el.closest('.tb-breathing-light-card'))
            : [];
          if (!titleEls.length && !subtitleEls.length) return;

          titleEls.forEach((el) => el.classList.add('tb-title-reveal'));
          subtitleEls.forEach((el) => el.classList.add('tb-subtitle-reveal'));
          if (titleEls.length) gsap.set(titleEls, titleFromVars);
          if (subtitleEls.length) gsap.set(subtitleEls, subtitleFromVars);

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: 'top 86%',
              once: true
            }
          });
          if (titleEls.length) {
            tl.to(titleEls, titleToVars);
          }
          if (subtitleEls.length) {
            tl.to(subtitleEls, subtitleToVars, titleEls.length ? '>-0.4' : 0);
          }
          section.dataset.tbTitleAnimInited = '1';
        });
      });

      const targets = this.querySelectorAll('.tb-animate');
      targets.forEach((el) => {
        if (el.dataset.tbAnimateInited === '1') return;
        el.classList.add('tb-subtitle-reveal');
        gsap.set(el, subtitleFromVars);
        gsap.to(el, {
          ...subtitleToVars,
          duration: 0.9,
          scrollTrigger: {
            trigger: el,
            start: 'top 86%',
            once: true
          }
        });
        el.dataset.tbAnimateInited = '1';
      });

      const bars = this.querySelectorAll('.tb-ultra-progress-bar');
      bars.forEach((bar) => {
        const targetW = bar.style.width || '0%';
        const pct = parseFloat(String(targetW).replace('%', '')) || 0;
        bar.style.width = '0%';
        gsap.to(bar, {
          width: pct + '%',
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: bar.closest('.tb-ultra-section') || bar,
            start: 'top 80%',
            once: true
          }
        });
      });

      const chargingPercents = this.querySelectorAll('.tb-charging-percent');
      chargingPercents.forEach((el) => {
        const raw = (el.textContent || '').trim();
        const match = raw.match(/^([^\d-]*)(-?\d+(?:\.\d+)?)(.*)$/);
        if (!match) return;
        const prefix = match[1] || '';
        const target = parseFloat(match[2]);
        const suffix = match[3] || '';
        if (!Number.isFinite(target)) return;
        const decimalPart = (match[2].split('.')[1] || '');
        const decimals = decimalPart.length;
        const state = { value: 0 };
        el.textContent = prefix + (decimals ? '0.' + '0'.repeat(decimals) : '0') + suffix;
        gsap.to(state, {
          value: target,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el.closest('.tb-charging-section') || el,
            start: 'top 80%',
            once: true
          },
          onUpdate: () => {
            const value = decimals ? state.value.toFixed(decimals) : Math.round(state.value);
            el.textContent = prefix + value + suffix;
          }
        });
      });

      const extSections = this.querySelectorAll('.tb-ext-section');
      extSections.forEach((ext) => {
        const overlay = ext.querySelector('.tb-ext-overlay');
        if (!overlay) return;
        gsap.set(overlay, { autoAlpha: 0 });
        gsap.to(overlay, {
          autoAlpha: 1,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: ext,
            start: 'top 80%',
            once: true
          }
        });
      });
    }
  }

  if (ExistingSection) {
    Object.getOwnPropertyNames(ThunderboltFormalSection.prototype).forEach((name) => {
      if (name !== 'constructor') {
        ExistingSection.prototype[name] = ThunderboltFormalSection.prototype[name];
      }
    });
    document.querySelectorAll('thunderbolt-formal-section').forEach((el) => {
      if (typeof el.connectedCallback === 'function') {
        el.__tbFormalInited = false;
        el.connectedCallback();
      }
    });
  } else {
    customElements.define('thunderbolt-formal-section', ThunderboltFormalSection);
  }
})();
